import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
  Title,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import {
  CircularProgress,
  Tab as MaterialTab,
  Tabs as MaterialTabs,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import classNames from 'classnames';
import { v4 as uuidv4 } from 'uuid';
import isEmpty from 'lodash/isEmpty';
import {
  blueVioletColor,
  goldColor,
  grayColor,
  successColor,
  turquoiseColor,
} from 'assets/jss/material-dashboard-pro-react';
import { formatNumberWithThinSpaces } from 'helpers/helpers';
import { currencyToSymbol as getCurrencySymbol } from 'utils/currencies';
import { isAfter, startOfToday } from 'date-fns';

import TabLabel from './TabLabel';
import { MetricsTabsContext } from '../contexts/MetricsTabsContext';
import MetricsLegend from './MetricsLegend';
import getExternalTooltipDate from './utils/getExternalTooltipDate';
import getExternalTooltipHtml from './utils/getExternalTooltipHtml';
import setExternalTooltip from './utils/setExternalTooltip';
import NoData from './NoData/NoData';

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
  Title
);

const chartColors = [
  goldColor,
  turquoiseColor,
  successColor[2],
  blueVioletColor,
];

const secondaryChartColors = ['#E66161', '#AA47D5', '#ED7505', '#871414'];

const hoverLinePlugin = {
  id: 'hoverLine',
  beforeDatasetsDraw: chart => {
    if (
      chart.tooltip &&
      // eslint-disable-next-line no-underscore-dangle -- FIXME
      chart.tooltip._active &&
      // eslint-disable-next-line no-underscore-dangle -- FIXME
      chart.tooltip._active.length
    ) {
      const { ctx } = chart;
      // eslint-disable-next-line no-underscore-dangle -- FIXME
      const activePoint = chart.tooltip._active[0];
      const { x } = activePoint.element;
      const { chartArea } = chart;

      ctx.save();
      ctx.beginPath();
      ctx.moveTo(x, chartArea.top);
      ctx.lineTo(x, chartArea.bottom);
      ctx.lineWidth = 1;
      ctx.strokeStyle = `${grayColor[4]}80`;
      ctx.stroke();
      ctx.restore();
    }
  },
};

const tooltipId = uuidv4();

const useStyles = makeStyles({
  tabRoot: {
    textTransform: 'inherit',
    textAlign: 'inherit',
    fontSize: '16px',
    fontWeight: '500',
    borderBottom: `1px solid ${grayColor[4]}`,
  },
  labelContainer: {
    padding: '8px 0 22px 14px',
  },
  indicator: {
    height: '4px',
  },
  indicatorColor0: {
    backgroundColor: chartColors[0],
  },
  indicatorColor1: {
    backgroundColor: chartColors[1],
  },
  indicatorColor2: {
    backgroundColor: chartColors[2],
  },
  indicatorColor3: {
    backgroundColor: chartColors[3],
  },
  chartWrapper: {
    position: 'relative',
    marginTop: '34px',
    height: '365px',
  },
  spinnerWrapper: {
    position: 'absolute',
    display: 'grid',
    placeItems: 'center',
    inset: '0',
  },
});

const MetricsChart = ({
  isLoading,
  statistics,
  renderLabels,
  renderComparisonsLabels,
}) => {
  const { t, i18n } = useTranslation();
  const [reloadKey, setReloadKey] = useState(0);
  const { metricsActiveIndex, setMetricsActiveIndex } =
    useContext(MetricsTabsContext);
  const classes = useStyles();
  const currency = useSelector(
    ({ Brands }) => Brands.brand.multinational.currency
  );
  const currencySymbol = getCurrencySymbol(currency);
  const [firstStatistics, secondStatistics] = statistics;

  if (isLoading) {
    return (
      <div className={classes.spinnerWrapper}>
        <CircularProgress />
      </div>
    );
  }

  if (isEmpty(firstStatistics)) {
    return null;
  }

  const metricsData = [
    {
      title: t('dashboard.metrics.ordersIncome'),
      value: formatNumberWithThinSpaces(firstStatistics.ordersIncome.total),
      statisticsKey: 'ordersIncome',
      valueUnit: currencySymbol,
      getValueSuffix: count =>
        t('dashboard.metrics.ordersIncomeCount', { count }),
    },
    {
      title: t('dashboard.metrics.ordersCount'),
      value: formatNumberWithThinSpaces(firstStatistics.ordersCount.total),
      statisticsKey: 'ordersCount',
      getValueSuffix: count => t('dashboard.metrics.ordersCount', { count }),
    },
    {
      title: t('dashboard.metrics.orderBags'),
      value: formatNumberWithThinSpaces(firstStatistics.orderBags.total),
      statisticsKey: 'orderBags',
      getValueSuffix: count => t('dashboard.metrics.orderBags', { count }),
    },
    {
      title: t('dashboard.metrics.deliveryBags'),
      value: formatNumberWithThinSpaces(firstStatistics.deliveryBags.total),
      statisticsKey: 'deliveryBags',
      getValueSuffix: count => t('dashboard.metrics.deliveryBags', { count }),
    },
  ];

  const currentStatisticsKey = metricsData[metricsActiveIndex].statisticsKey;

  const hasFirstStatisticsDataToShow =
    firstStatistics[currentStatisticsKey].total !== 0;
  const hasSecondStatisticsDataToShow = !!(
    secondStatistics && secondStatistics[currentStatisticsKey].total !== 0
  );
  const hasDataToShow =
    hasFirstStatisticsDataToShow || hasSecondStatisticsDataToShow;

  const isSingleDataPoint = Object.values(firstStatistics).every(
    stat => stat.dataSeries.length === 1
  );
  const activeMetricsData = metricsData[metricsActiveIndex];
  const { statisticsKey } = activeMetricsData;

  const formattedDates = firstStatistics[statisticsKey].dataSeries.map(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- FIXME
    ({ dateFrom, dateTo }, index) => {
      const secondDateFrom =
        secondStatistics?.[statisticsKey]?.dataSeries?.[index]?.dateFrom;

      const shouldCompare =
        renderComparisonsLabels && secondDateFrom !== undefined;

      return shouldCompare
        ? renderComparisonsLabels({
            firstDate: dateFrom,
            secondDate: secondDateFrom,
          })
        : renderLabels({ dateFrom });
    }
  );

  // eslint-disable-next-line no-shadow -- FIXME
  const getDatasets = statisticsKey => ({
    labels: formattedDates,
    datasets: statistics.map((stats, statIndex) => {
      const color =
        statIndex === 0
          ? chartColors[metricsActiveIndex]
          : secondaryChartColors[statIndex - 1];

      const { title, valueUnit, getValueSuffix } = activeMetricsData;
      const { dateFrom } = stats[statisticsKey].dataSeries[0];
      const { dateTo } = stats[statisticsKey].dataSeries.at(-1);
      const data = stats[statisticsKey].dataSeries.map(
        // eslint-disable-next-line no-shadow -- FIXME
        ({ value, dateFrom }) => {
          if (isAfter(new Date(dateFrom), startOfToday())) {
            return null;
          }
          return value;
        }
      );

      const isOnlyTodayData = data.filter(stat => stat !== null).length === 1;

      return {
        legendLabel: {
          datesRange: [dateFrom, dateTo],
          total: stats[statisticsKey].total,
        },
        data: isSingleDataPoint && data[0] === 0 ? {} : data,
        pointRadius: isSingleDataPoint || isOnlyTodayData ? 7 : 0,
        hitRadius: 7,
        hoverRadius: 7,
        borderColor: color,
        backgroundColor: color,
        borderDash: statIndex === 0 ? [] : [8, 8],
        borderWidth: 3,
        tooltipTitle: title,
        getValueSuffix,
        valueUnit,
        statisticsSource: stats[statisticsKey],
      };
    }),
  });

  const formatMetricsValue = value =>
    new Intl.NumberFormat(i18n.language).format(value);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'nearest',
      intersect: false,
    },
    plugins: {
      legend: {
        display: false,
      },
      datalabels: { display: false },
      tooltip: {
        enabled: false,
        external: ({ chart, tooltip }) => {
          if (!tooltip.dataPoints) {
            return;
          }

          const [
            {
              raw,
              dataIndex,
              dataset: {
                borderColor,
                getValueSuffix,
                statisticsSource,
                tooltipTitle,
                valueUnit,
              },
            },
          ] = tooltip.dataPoints;

          const { dateFrom, dateTo } = statisticsSource.dataSeries[dataIndex];

          setExternalTooltip({
            tooltipId,
            chart,
            tooltip,
            tooltipInnerHTML: getExternalTooltipHtml({
              title: tooltipTitle,
              color: borderColor,
              content: [
                {
                  text: getExternalTooltipDate(dateFrom, dateTo),
                },
                {
                  highlightedText: `${formatMetricsValue(raw)}${
                    valueUnit ? ` ${valueUnit}` : ''
                  }`,
                  text: getValueSuffix(raw),
                },
              ],
            }),
          });
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: { drawBorder: false, color: grayColor[4], drawTicks: false },
        ticks: {
          color: grayColor[20],
          font: { size: 12, weight: 400 },
          callback: (value, index) =>
            index === 0 ? '' : formatMetricsValue(value),
          padding: 6,
        },
      },
      x: {
        offset: isSingleDataPoint,
        grid: {
          drawOnChartArea: false,
          drawBorder: false,
          color: grayColor[4],
        },
        ticks: {
          color: grayColor[20],
          font: { size: 12, weight: 400 },
          padding: 4,
          maxRotation: 0,
          minRotation: 0,
          autoSkipPadding: 10,
        },
      },
    },
  };

  const handleChange = (_, index) => {
    setReloadKey(prevKey => prevKey + 1);
    setMetricsActiveIndex(index);
  };

  return (
    <>
      <MaterialTabs
        value={metricsActiveIndex}
        onChange={handleChange}
        variant="fullWidth"
        indicatorColor="secondary"
        classes={{
          root: classes.tabsRoot,
          indicator: classNames(
            classes.indicator,
            [
              classes.indicatorColor0,
              classes.indicatorColor1,
              classes.indicatorColor2,
              classes.indicatorColor3,
            ][metricsActiveIndex]
          ),
        }}
      >
        {metricsData.map(
          // eslint-disable-next-line no-shadow -- FIXME
          ({ title, value, valueUnit, statisticsKey }, index) => (
            <MaterialTab
              label={
                <TabLabel
                  title={title}
                  value={`${value}${valueUnit ? ` ${valueUnit}` : ''}`}
                  isActive={index === metricsActiveIndex}
                />
              }
              key={statisticsKey}
              classes={{
                root: classes.tabRoot,
                labelContainer: classes.labelContainer,
              }}
            />
          )
        )}
      </MaterialTabs>
      {statistics.length > 1 && (
        <MetricsLegend
          datasets={getDatasets(currentStatisticsKey).datasets}
          valueUnit={metricsData[metricsActiveIndex].valueUnit}
        />
      )}
      <div key={reloadKey} className={classes.chartWrapper}>
        <Line
          data={getDatasets(currentStatisticsKey)}
          options={options}
          plugins={[hoverLinePlugin]}
        />
        {!hasDataToShow && <NoData />}
      </div>
    </>
  );
};

export default MetricsChart;
